/**
 * Consent Management Screen
 * 
 * Allows users to manage their consent preferences for GDPR compliance
 */

import { useState, useEffect } from 'react';
import { View, ScrollView, Switch, Alert, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubHeading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import {
  getConsentPreferences,
  updateConsentPreference,
  type ConsentType,
  type ConsentPreference,
  type ConsentStatus,
  getCurrentPrivacyPolicyVersion,
} from '@/services/consentManagement';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { TripErrorBoundary } from '@/utils/errorBoundary';

type ConsentItem = {
  type: ConsentType;
  label: string;
  description: string;
  icon: keyof typeof Ionicons.glyphMap;
  required?: boolean; // Some consents may be required for app functionality
};

const CONSENT_ITEMS: ConsentItem[] = [
  {
    type: 'analytics',
    label: 'Analytics & Usage Data',
    description: 'Allow us to collect anonymous usage data to improve the app. This includes feature usage, performance metrics, and error reports.',
    icon: 'analytics-outline',
  },
  {
    type: 'marketing',
    label: 'Marketing Communications',
    description: 'Receive emails about new features, travel tips, and special offers. You can unsubscribe at any time.',
    icon: 'mail-outline',
  },
  {
    type: 'email_access',
    label: 'Email Access',
    description: 'Access your emails to automatically detect and import travel bookings. We only read emails with travel confirmations.',
    icon: 'mail-open-outline',
  },
  {
    type: 'cloud_backup',
    label: 'Cloud Backup',
    description: 'Store your data in the cloud for access across devices. Your data is encrypted and secure.',
    icon: 'cloud-outline',
  },
  {
    type: 'location',
    label: 'Location Services',
    description: 'Use your location to show you on maps, provide navigation, and send location-based notifications.',
    icon: 'location-outline',
  },
  {
    type: 'notifications',
    label: 'Push Notifications',
    description: 'Receive notifications about trip updates, reminders, and important alerts.',
    icon: 'notifications-outline',
  },
];

function ConsentManagementScreenInner() {
  const theme = useTheme();
  const router = useRouter();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [preferences, setPreferences] = useState<Record<ConsentType, ConsentPreference | undefined>>({} as any);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<ConsentType | null>(null);

  const closeScreen = () => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/more?tab=profile');
  };

  useEffect(() => {
    if (!firebaseUser?.uid) {
      router.replace('/(onboarding)/sign-in');
      return;
    }

    loadPreferences();
  }, [firebaseUser?.uid, router]);

  const loadPreferences = async () => {
    if (!firebaseUser?.uid) return;

    try {
      setLoading(true);
      const prefs = await getConsentPreferences(firebaseUser.uid);
      setPreferences(prefs as any);
    } catch (error) {
      console.error('[ConsentManagement] Error loading preferences:', error);
      Alert.alert('Error', 'Failed to load consent preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (type: ConsentType, currentStatus: ConsentStatus | undefined) => {
    if (!firebaseUser?.uid) {
      Alert.alert('Error', 'You must be logged in to manage consent preferences.');
      return;
    }
    if (updating) {
      Alert.alert('Saving in progress', 'Still saving your last change. Try again in a moment.');
      return;
    } // Prevent multiple simultaneous updates

    const newStatus: ConsentStatus = currentStatus === 'granted' ? 'withdrawn' : 'granted';

    // Show confirmation for withdrawing consent
    if (newStatus === 'withdrawn') {
      const item = CONSENT_ITEMS.find(i => i.type === type);
      const confirmed = await new Promise<boolean>((resolve) => {
        Alert.alert(
          'Withdraw Consent',
          `Are you sure you want to withdraw consent for ${item?.label}? This may affect app functionality.`,
          [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Withdraw', style: 'destructive', onPress: () => resolve(true) },
          ]
        );
      });

      if (!confirmed) {
        // User cancelled - don't proceed
        return;
      }
    }

    try {
      setUpdating(type);
      const version = getCurrentPrivacyPolicyVersion();
      await updateConsentPreference(firebaseUser.uid, type, newStatus, version);
      
      // Reload preferences from server to ensure we have the latest data
      const updatedPrefs = await getConsentPreferences(firebaseUser.uid);
      setPreferences(updatedPrefs as any);

      // Show success message
      const item = CONSENT_ITEMS.find(i => i.type === type);
      Alert.alert(
        'Consent Updated',
        `Your consent preference for ${item?.label} has been updated.`
      );
    } catch (error: any) {
      console.error('[ConsentManagement] Error updating consent:', error);
      const errorMessage = humanizeAsyncError(
        error,
        'We couldn’t update this preference right now. Please try again.',
      );
      Alert.alert('Unable to update preference', errorMessage);
      
      // Reload preferences to ensure UI is in sync
      try {
        const currentPrefs = await getConsentPreferences(firebaseUser.uid);
        setPreferences(currentPrefs as any);
      } catch (reloadError) {
        console.error('[ConsentManagement] Error reloading preferences:', reloadError);
      }
    } finally {
      setUpdating(null);
    }
  };

  const getConsentStatus = (type: ConsentType): ConsentStatus | undefined => {
    return preferences[type]?.status;
  };

  const isConsentGranted = (type: ConsentType): boolean => {
    return getConsentStatus(type) === 'granted';
  };

  return (
    <Screen
      title="Consent Management"
      subtitle="Manage your data processing and communication preferences"
      headerAction={
        <Button
          label="Done"
          variant="ghost"
          onPress={closeScreen}
        />
      }
    >
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(2) }}
      >
        <Card variant="outlined">
          <View style={{ gap: theme.spacing(1.5) }}>
            <SubHeading>Your Rights</SubHeading>
            <Body color={theme.colors.textSecondary}>
              Under GDPR, you have the right to control how your personal data is processed. You can grant or withdraw consent for different types of data processing at any time.
            </Body>
            <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
              Withdrawing consent may affect app functionality. Required features (like authentication) cannot be disabled.
            </Body>
          </View>
        </Card>

        {CONSENT_ITEMS.map((item) => {
          const isGranted = isConsentGranted(item.type);
          const isUpdating = updating === item.type;
          const status = getConsentStatus(item.type);

          return (
            <Card key={item.type} variant="outlined">
              <View style={{ gap: theme.spacing(1.5) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), flex: 1 }}>
                    <Ionicons name={item.icon} size={24} color={theme.colors.primary} />
                    <View style={{ flex: 1 }}>
                      <SubHeading>{item.label}</SubHeading>
                      <Body color={theme.colors.textSecondary} style={{ fontSize: 13, marginTop: theme.spacing(0.5) }}>
                        {item.description}
                      </Body>
                    </View>
                  </View>
                  {isUpdating ? (
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                  ) : (
                    <Switch
                      value={isGranted}
                      onValueChange={() => handleToggle(item.type, status)}
                      trackColor={{
                        false: theme.colors.borderMuted,
                        true: theme.colors.primary + '50',
                      }}
                      thumbColor={isGranted ? theme.colors.primary : theme.colors.textSecondary}
                    />
                  )}
                </View>

                {preferences[item.type] && (
                  <View style={{ marginTop: theme.spacing(1), paddingTop: theme.spacing(1), borderTopWidth: 1, borderTopColor: theme.colors.borderMuted }}>
                    <Caption color={theme.colors.textSecondary}>
                      {preferences[item.type]?.status === 'granted' &&
                      preferences[item.type]?.grantedAt != null
                        ? `Granted: ${new Date(preferences[item.type]!.grantedAt as string | number | Date).toLocaleDateString()}`
                        : ''}
                      {preferences[item.type]?.status === 'withdrawn' &&
                      preferences[item.type]?.withdrawnAt != null
                        ? `Withdrawn: ${new Date(preferences[item.type]!.withdrawnAt as string | number | Date).toLocaleDateString()}`
                        : ''}
                      {preferences[item.type]?.version && (
                        ` • Policy version: ${preferences[item.type]?.version}`
                      )}
                    </Caption>
                  </View>
                )}
              </View>
            </Card>
          );
        })}

        <Card variant="outlined">
          <View style={{ gap: theme.spacing(1.5) }}>
            <SubHeading>Need Help?</SubHeading>
            <Body color={theme.colors.textSecondary}>
              If you have questions about how we process your data or want to exercise your rights, please contact our Data Protection Officer at:
            </Body>
            <Button
              label="Contact DPO"
              variant="secondary"
              onPress={() => {
                Alert.alert(
                  'Contact Data Protection Officer',
                  'Please email privacy@tripvio.app with your inquiry. We will respond within 30 days as required by GDPR.'
                );
              }}
            />
          </View>
        </Card>
      </ScrollView>
    </Screen>
  );
}

export default function ConsentManagementScreen() {
  return (
    <TripErrorBoundary name="ConsentManagement">
      <ConsentManagementScreenInner />
    </TripErrorBoundary>
  );
}
